/**
 * Collection constraints configuration
 * Each constraint can define:
 * - criteria: object of fields to match (e.g. {crop: "fallow"})
 * - daily_limit: object with 'proportion' (0.0-1.0) and 'min_samples' (N)
 *   Means: If daily submissions >= N, max proportion of submissions can match criteria.
 * - rolling_window: object with 'size' (N) and 'max_count' (M)
 *   Means: In the last N submissions (including current), max M can match criteria.
 */
export const COLLECTION_CONSTRAINTS = [
  {
    name: "Fallow",
    criteria: { crop: "fallow" },
    daily_limit: { proportion: 0.2, min_samples: 4 }, // 20% max once we have >=4 samples
    rolling_window: { size: 10, max_count: 2 },
  },
  // Add more constraints here as needed
  // Example for a specific crop/phenology combination:
  // {
  //   name: "Stage 6 Corn",
  //   criteria: { crop: "corn", phenology: "6" },
  //   rolling_window: { size: 10, max_count: 3 },
  // },
];

function matchesCriteria(cropData, criteria) {
  for (const [key, value] of Object.entries(criteria)) {
    const cropValue = cropData[key] || "";

    // Case-insensitive comparison for strings
    if (typeof cropValue === "string" && typeof value === "string") {
      if (cropValue.toLowerCase() !== value.toLowerCase()) {
        return false;
      }
    } else if (cropValue !== value) {
      return false;
    }
  }
  return true;
}

export function validateCollectionConstraints(currentData, submittedData) {
  const crops = currentData.crops || [];
  if (crops.length === 0) {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const todaySubmissions = submittedData.filter((submission) => {
    const submissionDate = new Date(submission.date);
    submissionDate.setHours(0, 0, 0, 0);
    return submissionDate.getTime() === today.getTime();
  });

  for (const constraint of COLLECTION_CONSTRAINTS) {
    const { criteria, rolling_window, daily_limit, name } = constraint;

    // Check if any crop matches this constraint's criteria
    const matchingIndices = [];
    crops.forEach((c, index) => {
      if (matchesCriteria(c, criteria)) {
        matchingIndices.push(index);
      }
    });

    if (matchingIndices.length === 0) {
      continue;
    }

    // Rolling Window Check
    if (rolling_window) {
      const { size, max_count } = rolling_window;

      // Only enforce rolling window if user has at least window_size submissions today
      // This prevents rejecting first few submissions
      if (todaySubmissions.length >= size - 1) {
        // Get last N-1 submissions (current will be Nth)
        const recentSubmissions = todaySubmissions.slice(-(size - 1));

        let matchCount = 0;
        for (const submission of recentSubmissions) {
          const submissionCrops = submission.crops || [];
          if (
            submissionCrops.length > 0 &&
            submissionCrops.some((c) => matchesCriteria(c, criteria))
          ) {
            matchCount++;
          }
        }

        // Add 1 for current submission
        if (matchCount + 1 > max_count) {
          const desc =
            name ||
            Object.entries(criteria)
              .map(([k, v]) => `${k}=${v}`)
              .join(", ");

          return {
            constraint,
            matchingIndices,
            message: `Limit exceeded for ${desc}: Max ${max_count} allowed in the last ${size} submissions.`,
          };
        }
      }
    }

    // Daily Limit Check
    if (daily_limit != null) {
      const { proportion, min_samples } = daily_limit;
      const todayTotal = todaySubmissions.length;

      // Only enforce daily limit if user has made at least min_samples submissions today
      if (todayTotal >= min_samples) {
        // Count how many of today's submissions match criteria
        let todayMatches = 0;
        for (const submission of todaySubmissions) {
          const submissionCrops = submission.crops || [];
          if (
            submissionCrops.length > 0 &&
            submissionCrops.some((c) => matchesCriteria(c, criteria))
          ) {
            todayMatches++;
          }
        }

        // Add 1 for current submission
        const newTotal = todayTotal + 1;
        const newMatches = todayMatches + 1;

        if (newMatches / newTotal > proportion) {
          const desc =
            name ||
            Object.entries(criteria)
              .map(([k, v]) => `${k}=${v}`)
              .join(", ");

          const percentLimit = Math.round(proportion * 100);
          return {
            constraint,
            matchingIndices,
            message: `Daily limit exceeded for ${desc}: Maximum ${percentLimit}% of daily submissions allowed.`,
          };
        }
      }
    }
  }

  return null;
}
